import { ToastsErrorBottomLeft } from 'Shared/Toast';
import axios from 'axios';
import { IdTokenClaims, JwtClaims, SigninResponse, User, UserManagerSettingsStore, UserProfile } from 'oidc-client-ts';
import { UserManager } from 'oidc-client-ts';

import { _userManagerConfig } from '../Components/UserManagerInitializer';
import { IUser } from '../Models/IUser';
import { IUserManagerConfig } from '../Models/IUserManagerConfig';
import { GetUserManagerSettings } from '../Utils/GetUserManagerSettings';

const CreateUserManager = () => {
  if (_userManagerConfig === undefined) throw new Error('UserManagerConfig is null');

  const props: IUserManagerConfig = _userManagerConfig;

  const initialUserManagerConfig = GetUserManagerSettings(props);

  return new UserManager(initialUserManagerConfig);
};

export const useUserManager = () => {
  if (_userManagerConfig === undefined) throw new Error('UserManagerConfig is null');

  const props: IUserManagerConfig = _userManagerConfig;

  const ssoURL = props.ssoURL ?? '';
  const ssoUserInfoEndpoint = props.ssoUserInfoEndpoint;
  const wepodURL = props.wepodURL ?? '';
  const mainAddress = props.mainAddress ?? '';
  const autoLogin = props.autoLogin ?? '';
  const exchangeUrl = props.exchangeUrl ?? '';
  const renewUrl = props.renewUrl ?? '';

  const userManagerFinal = CreateUserManager();

  const userManagerConfig = GetUserManagerSettings(props);
  const settings = new UserManagerSettingsStore(userManagerConfig);

  const getUserClaims = async () => {
    const user: any = await userManagerFinal.getUser();
    if (!user) {
      signIn();
    }

    return props.userMapper(user);
  };

  const getUser = async () => {
    const user = await userManagerFinal.getUser();
    if (!user) {
      signIn();
    }
    return user;
  };

  const userIsLogin = async () => {
    const user = await userManagerFinal.getUser();
    return user && !user.expired;
  };

  const signIn = async (redirectPath?: string) => {
    clearUser();
    if (wepodURL != '') {
      clearUser();
      window.location.replace(wepodURL);
    } else {
      userManagerFinal.getUser().then(async (user) => {
        if (user === null || user.expired) {
          await userManagerFinal.signinRedirect({
            state: { path: redirectPath ?? '/' },
          });
        } else if (user) {
          localStorage.setItem('token', user.access_token);
        }
      });
    }
  };

  const signOut = async () => {
    await userManagerFinal.removeUser();
    await localStorage.clear();
    await userManagerFinal.clearStaleState();
    await userManagerFinal.signoutRedirect();
  };

  const signinRedirectCallback = () => {
    userManagerFinal
      .signinRedirectCallback()
      .then((user) => {
        successSigninCallback(user);
      })
      .catch((error) => {
        errorSigninCallback(error);
      });
  };

  const successSigninCallback = (user: any) => {
    localStorage.setItem('token', user.access_token);
    const redirectPath = user?.state?.path ?? mainAddress;
    const a = document.createElement('a');
    a.href = redirectPath;
    a.click();
  };

  const errorSigninCallback = (error: any) => {
    ToastsErrorBottomLeft([error]);
    const a = document.createElement('a');
    a.href = mainAddress;
    a.click();
  };

  const signoutRedirectCallback = () => {
    userManagerFinal
      .signoutRedirectCallback()
      .then((user) => successSignoutCallback(user))
      .catch((error) => errorSignoutCallback(error));
  };

  const successSignoutCallback = (response: any) => {
    ToastsErrorBottomLeft([response]);
    userManagerFinal.removeUser();
    localStorage.clear();
    userManagerFinal.clearStaleState();
    window.location.href = mainAddress;
  };

  const errorSignoutCallback = (error: any) => {
    ToastsErrorBottomLeft([error]);
    window.location.href = mainAddress;
  };

  const clearUser = async () => {
    await userManagerFinal.removeUser();
    localStorage.removeItem('token');
    await userManagerFinal.clearStaleState();
  };

  const signinSilent = async () => {
    userManagerFinal
      .signinSilent()
      .then((response) => {
        localStorage.removeItem('token');
        localStorage.setItem('token', response?.access_token as string);
      })
      .catch(() => {
        signIn();
      });
  };

  const doIfUserTokenIsNotExpire = (task: taskType) => {
    getUser().then((user) => {
      if (user && !user?.expired) {
        task(user);
      }
    });
  };

  // #region Pod Login Callback
  const podLoginCallback = async () => {
    clearUser();
    const signinResponse = new SigninResponse(urlReadParams(window.location.href, 'query'));
    await exchangeCode(signinResponse);
    await processClaims(signinResponse);
    const user = await buildUser(signinResponse);
    autoLoginSaveState();
    localStorage.setItem('token', user.access_token);
  };

  const urlReadParams = (url: string, responseMode: 'query' | 'fragment' = 'query'): URLSearchParams => {
    if (!url) throw new TypeError('Invalid URL');
    const parsedUrl = new URL(url, window.location.origin);
    const params = parsedUrl[responseMode === 'fragment' ? 'hash' : 'search'];
    return new URLSearchParams(params.slice(1));
  };

  const exchangeCode = async (response: SigninResponse) => {
    if (response.code) {
      const tokenResponse = await axios.post(exchangeUrl, {
        code: response.code,
        redirectUri: window.location.origin + autoLogin,
      });
      if (tokenResponse.status == 400) {
        throw new Error('Error_On_Exchange_Code');
      }

      Object.assign(response, tokenResponse.data);
    }
  };

  const processClaims = async (response: SigninResponse) => {
    response.profile = filterProtocolClaims(response.profile);
    const claims = await getClaims(response.access_token);
    response.profile = mergeClaims(response.profile, filterProtocolClaims(claims as IdTokenClaims));
  };

  const filterProtocolClaims = (claims: UserProfile): UserProfile => {
    const result = { ...claims };
    if (settings.filterProtocolClaims) {
      let protocolClaims;
      if (Array.isArray(settings.filterProtocolClaims)) {
        protocolClaims = settings.filterProtocolClaims;
      } else {
        protocolClaims = DefaultProtocolClaims;
      }
      for (const claim of protocolClaims) {
        if (!InternalRequiredProtocolClaims.includes(claim)) {
          delete result[claim];
        }
      }
    }
    return result;
  };

  const getClaims = async (token: string): Promise<JwtClaims> => {
    const url = ssoURL + ssoUserInfoEndpoint;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status == 400) {
      throw new Error('Error_On_User_Profile');
    }
    return response.data;
  };

  const mergeClaims = (claims1: UserProfile, claims2: JwtClaims): UserProfile => {
    const result = { ...claims1 };
    for (const [claim, values] of Object.entries(claims2)) {
      for (const value of Array.isArray(values) ? values : [values]) {
        const previousValue = result[claim];
        if (!previousValue) {
          result[claim] = value;
        } else if (Array.isArray(previousValue)) {
          if (!previousValue.includes(value)) {
            previousValue.push(value);
          }
        } else if (result[claim] !== value) {
          if (typeof value === 'object' && settings.mergeClaims) {
            result[claim] = mergeClaims(previousValue as UserProfile, value);
          } else {
            result[claim] = [previousValue, value];
          }
        }
      }
    }
    return result;
  };

  const buildUser = async (signinResponse: SigninResponse) => {
    const user = new User(signinResponse);
    await storeUser(user);
    userManagerFinal.events.load(user);
    return user;
  };

  const storeUser = async (user: IUser | null) => {
    const userStoreKey = `user:${settings.authority}:${settings.client_id}`;
    if (user) {
      const storageString = user.toStorageString();
      await settings.userStore.set(userStoreKey, storageString);
    } else {
      await settings.userStore.remove(userStoreKey);
    }
  };

  const autoLoginSaveState = async () => {
    const state = new URLSearchParams(window.location.search).get('state');

    if (state) {
      const result = decodeURI(state)
        .replace(/^&/, '')
        .split('&')
        .reduce((acc, item) => {
          const keyValue = item.split('=');
          return { ...acc, [keyValue[0]]: keyValue[1] };
        }, {});

      localStorage.setItem('AutoLoginState', JSON.stringify(result));
    }
  };

  const podSigninSilent = async () => {
    const user = await loadUser();
    if (user?.refresh_token) {
      const state = new RefreshState(user as Required<User>);
      await refreshToken(state);
    } else {
      await signIn();
    }
  };

  const loadUser = async (): Promise<User | null> => {
    const storageString = await settings.userStore.get(`user:${settings.authority}:${settings.client_id}`);
    if (storageString) {
      return User.fromStorageString(storageString);
    }
    return null;
  };

  const refreshToken = async (state: RefreshState): Promise<User> => {
    const result = await axios.post(renewUrl, {
      refreshToken: state.refresh_token,
    });

    if (result.status == 400) {
      throw new Error('Error_On_Refresh_Token');
    }

    const response = new SigninResponse(new URLSearchParams());
    Object.assign(response, result.data);

    localStorage.removeItem('token');
    localStorage.setItem('token', response?.access_token as string);

    const temp = { ...state, ...response };
    temp.profile = state.profile;

    const user = new User(temp);
    await storeUser(user);
    userManagerFinal.events.load(user);

    return user;
  };
  // #endregion

  return {
    signIn,
    clearUser,
    signinRedirectCallback,
    signoutRedirectCallback,
    signOut,
    getUser,
    userIsLogin,
    getUserClaims,
    successSigninCallback,
    errorSigninCallback,
    signinSilent,
    doIfUserTokenIsNotExpire,
    podLoginCallback,
    podSigninSilent,
    userManager: userManagerFinal,
  };
};

export interface ExchangeCodeArgs {
  client_id?: string;
  client_secret?: string;
  redirect_uri?: string;

  grant_type?: string;
  code: string;
  code_verifier?: string;
}

const DefaultProtocolClaims = ['nbf', 'jti', 'auth_time', 'nonce', 'acr', 'amr', 'azp', 'at_hash'] as const;

const InternalRequiredProtocolClaims = ['sub', 'iss', 'aud', 'exp', 'iat'];

type taskType = (user: IUser | null) => void;

export class RefreshState {
  /** custom "state", which can be used by a caller to have "data" round tripped */
  public readonly data: unknown | undefined;

  public readonly refresh_token: string;
  public readonly id_token?: string;
  public readonly session_state: string | null;
  public readonly scope?: string;
  public readonly profile: UserProfile;

  constructor(args: {
    refresh_token: string;
    id_token?: string;
    session_state: string | null;
    scope?: string;
    profile: UserProfile;

    state?: unknown;
  }) {
    this.refresh_token = args.refresh_token;
    this.id_token = args.id_token;
    this.session_state = args.session_state;
    this.scope = args.scope;
    this.profile = args.profile;

    this.data = args.state;
  }
}
